// src/automation/components/EditableBlock.js
//
// A self-contained block card with per-block inline editing:
//   • read-only view (labeled key -> value) with a small "Edit" button
//   • click Edit -> fields become typed inputs (text / number / boolean /
//     array / nested object) with Save / Cancel
//   • Save calls onSave(nextValue); the parent merges it into the full config
//     and persists to S3. Per-block saving / success / error states.
//
// Used for every config block (General, each advanced block like Extraction /
// RCC / ARRM, etc.), so each is independently editable at field level.

import React, { useState } from "react";
import { Pencil, Save, X, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

// ── typed inputs ───────────────────────────────────────────────────────────

// Fields that hold semicolon-separated recipient lists — edited one-per-line.
const RECIPIENT_FIELDS = new Set(["to", "cc", "bcc"]);

function AutoField({ label, value, onChange }) {
  let control;
  let wide = false;

  // Raw text buffer for recipient textareas, so the user's in-progress typing
  // (including trailing blank lines from a just-pressed Enter) isn't lost when
  // the value is normalized to a ``;``-joined string on each change.
  const [emailDraft, setEmailDraft] = useState(null);

  const key = String(label || "").toLowerCase();
  const isRecipient = RECIPIENT_FIELDS.has(key);
  const isLongString = typeof value === "string" && value.length > 60;

  if (typeof value === "boolean") {
    control = (
      <label className="auto-eb-toggle">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
        <span>{value ? "true" : "false"}</span>
      </label>
    );
  } else if (typeof value === "number") {
    control = (
      <input
        className="auto-eb-input"
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
    );
  } else if (isRecipient) {
    // Recipient list: a roomy textarea, one address per line. Stored back as a
    // semicolon-separated string (the format the backend expects).
    wide = true;
    // Edit the RAW text the user types (keep their blank lines / caret position)
    // so pressing Enter on the last line always opens a new line. We only
    // normalize to a ``;``-joined string when handing the value back up.
    const stored = String(value ?? "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .join("\n");
    // Use the in-progress draft text if present, else the stored value.
    const text = emailDraft != null ? emailDraft : stored;
    const count = text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean).length;
    // Auto-grow: always leave at least one blank line of headroom beyond the
    // current content so the caret stays visible when you press Enter.
    const lineCount = text.split("\n").length;
    const rows = Math.min(20, Math.max(4, lineCount + 2));
    control = (
      <>
        <textarea
          className="auto-eb-textarea auto-eb-textarea--emails"
          rows={rows}
          value={text}
          placeholder="one email per line — press Enter for each new address"
          onChange={(e) => {
            const raw = e.target.value;
            setEmailDraft(raw);
            // Normalize to the ``;``-joined form the backend expects, but DON'T
            // strip the user's current typing/newlines from the textarea itself.
            const joined = raw
              .split(/[\n;,]/)
              .map((s) => s.trim())
              .filter(Boolean)
              .join(";");
            onChange(joined);
          }}
        />
        <span className="auto-eb-field-hint">{count} recipient{count === 1 ? "" : "s"} · one per line</span>
      </>
    );
  } else if (Array.isArray(value)) {
    const allScalar = value.every((v) => typeof v !== "object");
    control = allScalar ? (
      <input
        className="auto-eb-input"
        type="text"
        value={value.join(", ")}
        onChange={(e) => {
          const parts = e.target.value
            .split(",")
            .map((x) => x.trim())
            .filter((x) => x !== "");
          const nums = value.length && value.every((v) => typeof v === "number");
          onChange(nums ? parts.map(Number) : parts);
        }}
      />
    ) : (
      (wide = true),
      (
        <textarea
          className="auto-eb-textarea"
          rows={4}
          defaultValue={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              /* ignore until valid */
            }
          }}
        />
      )
    );
  } else if (value && typeof value === "object") {
    wide = true;
    control = (
      <textarea
        className="auto-eb-textarea"
        rows={5}
        defaultValue={JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            /* ignore until valid */
          }
        }}
      />
    );
  } else if (isLongString) {
    // Long single string (e.g. a long subject) — give it a full-width line.
    wide = true;
    control = (
      <input
        className="auto-eb-input"
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    control = (
      <input
        className="auto-eb-input"
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <label className={`auto-eb-field ${wide ? "auto-eb-field--wide" : ""}`}>
      <span className="auto-eb-field-label">{label}</span>
      {control}
    </label>
  );
}

// ── read-only rendering ──────────────────────────────────────────────────────

function displayValue(v) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

// ── block ────────────────────────────────────────────────────────────────────
//
// Props:
//   title     : card header
//   value     : the object this block represents (its own fields)
//   onSave    : async (nextValue) => void   — persists; may throw
//   editable  : default true
//   renderView: optional custom read-only renderer (value) => node
//   renderEdit: optional custom editor (draft, setDraft) => node

export default function EditableBlock({
  title,
  subtitle,
  value,
  onSave,
  editable = true,
  renderView,
  renderEdit,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState("idle"); // idle | saving | ok | err
  const [errMsg, setErrMsg] = useState(null);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(value ?? {})));
    setStatus("idle");
    setErrMsg(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setStatus("idle");
  };
  const save = async () => {
    setStatus("saving");
    setErrMsg(null);
    try {
      await onSave(draft);
      setStatus("ok");
      setEditing(false);
    } catch (e) {
      setStatus("err");
      setErrMsg(e?.message || "Save failed");
    }
  };

  const isObj = draft && typeof draft === "object" && !Array.isArray(draft);

  return (
    <div className="auto-cfg-block auto-eb">
      <div className="auto-eb-head">
        <h4 className="auto-cfg-block-title auto-eb-title">
          {title}
          {subtitle && <span className="auto-eb-sub"> {subtitle}</span>}
        </h4>
        {editable && !editing && (
          <button className="auto-eb-edit" onClick={startEdit}>
            <Pencil size={13} /> Edit
          </button>
        )}
        {editing && (
          <div className="auto-eb-actions">
            <button className="auto-eb-btn" onClick={cancel} disabled={status === "saving"}>
              <X size={13} /> Cancel
            </button>
            <button
              className="auto-eb-btn auto-eb-btn--primary"
              onClick={save}
              disabled={status === "saving"}
            >
              {status === "saving" ? (
                <RefreshCw size={13} className="auto-eb-spin" />
              ) : (
                <Save size={13} />
              )}
              Save
            </button>
          </div>
        )}
      </div>

      {status === "err" && (
        <div className="auto-eb-alert auto-eb-alert--err">
          <XCircle size={13} /> <span>{errMsg}</span>
        </div>
      )}
      {status === "ok" && !editing && (
        <div className="auto-eb-alert auto-eb-alert--ok">
          <CheckCircle2 size={13} /> <span>Saved.</span>
        </div>
      )}

      {editing ? (
        renderEdit ? (
          renderEdit(draft, setDraft)
        ) : isObj ? (
          <div className="auto-eb-grid">
            {Object.entries(draft).map(([k, v]) => (
              <AutoField
                key={k}
                label={k}
                value={v}
                onChange={(nv) => setDraft((d) => ({ ...d, [k]: nv }))}
              />
            ))}
          </div>
        ) : (
          <AutoField label={title} value={draft} onChange={setDraft} />
        )
      ) : renderView ? (
        renderView(value)
      ) : isObjView(value) ? (
        <dl className="auto-cfg-kv">
          {Object.entries(value).map(([k, v]) => (
            <div className="auto-cfg-kv-row" key={k}>
              <dt>{k}</dt>
              <dd>{displayValue(v)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="auto-cfg-text">{displayValue(value)}</p>
      )}
    </div>
  );
}

function isObjView(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

export { AutoField, displayValue };
